<!--
  TAILWIND SAFELIST - These classes must be listed so Tailwind JIT includes them:
  i-circle-flags-gb i-circle-flags-es i-circle-flags-br i-circle-flags-de
  i-circle-flags-fr i-circle-flags-it i-circle-flags-cn i-circle-flags-tr
-->
<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.1
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import {
  KAPTION_DELIVERY_MODES,
  resolveKaptionDeliveryMode,
} from 'dashboard/helper/kaptionDeliveryMode';
import KaptionSortMenu from './KaptionSortMenu.vue';
import wootConstants from 'dashboard/constants/globals';

// Props
const props = defineProps({
  // Dark mode control - explicit prop like original
  isDarkMode: {
    type: Boolean,
    default: true,
  },
  // Data props - will be connected to store in Phase 2
  favorites: {
    type: Array,
    default: () => [
      { id: 'fav-1', name: 'Product Team' },
      { id: 'fav-2', name: 'Sarah Chen' },
      { id: 'fav-3', name: 'Design Sync' },
    ],
  },
  folders: {
    type: Array,
    default: () => [
      { id: 'folder-1', name: 'Active Projects' },
      { id: 'folder-2', name: 'Needs Response' },
      { id: 'folder-3', name: 'This Week' },
    ],
  },
  labels: {
    type: Array,
    default: () => [
      { id: 'label-1', name: 'Work', color: '#6366f1' },
      { id: 'label-2', name: 'Personal', color: '#10b981' },
      { id: 'label-3', name: 'Urgent', color: '#ef4444' },
    ],
  },
  channels: {
    type: Array,
    default: () => [
      {
        id: 'channel-1',
        name: 'WhatsApp',
        count: 12,
        color: '#25D366',
        iconType: 'whatsapp',
      },
      {
        id: 'channel-2',
        name: 'Email',
        count: 8,
        color: '#EA4335',
        iconType: 'email',
      },
      {
        id: 'channel-3',
        name: 'Telegram',
        count: 3,
        color: '#0088CC',
        iconType: 'telegram',
      },
    ],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  assistants: {
    type: Array,
    default: () => [],
  },
  initialFilter: {
    type: String,
    default: 'all',
    validator: v =>
      [
        'all',
        'mine',
        'unassigned',
        'unattended',
        'mentions',
        'unread',
      ].includes(v),
  },
  initialStatusFilter: {
    type: String,
    default: 'open',
    validator: v => ['open', 'resolved', 'snoozed', 'all'].includes(v),
  },
  // Feature flag for settings panel
  showSettingsPanel: {
    type: Boolean,
    default: true,
  },
  // Disable cycling filter (when folders/filters are active)
  disableCyclingFilter: {
    type: Boolean,
    default: false,
  },
  // Whether we're on the "All Conversations" view (dashboard route with no category selected)
  // When true, shows extended filter options: all, unattended, mine, unassigned, mentions
  isAllConversationsView: {
    type: Boolean,
    default: false,
  },
  // Current conversation type (mention, unattended, etc.)
  conversationType: {
    type: String,
    default: '',
  },
  // Selected items - for showing contextual icons when filtering
  selectedTeam: {
    type: Object,
    default: null,
  },
  selectedChannel: {
    type: Object,
    default: null,
  },
  selectedLabel: {
    type: Object,
    default: null,
  },
  selectedFolder: {
    type: Object,
    default: null,
  },
  selectedAssistant: {
    type: Object,
    default: null,
  },
  // Current sort order for conversations
  currentSort: {
    type: String,
    default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
});
// Emits
const emit = defineEmits([
  'favoriteClick',
  'folderClick',
  'labelClick',
  'channelClick',
  'teamClick',
  'assistantClick',
  'filterChange',
  'statusFilterChange',
  'sortChange',
  'toggleFavorite',
  'navigateToMentions',
  'navigateToUnattended',
  'navigateToAllConversations',
]);
const { t, locale } = useI18n();
const { enabledLanguages } = useConfig();
const { currentAccount } = useAccount();

// NOTICE: KAPTION-INTEGRATION - Use composable for reactive favorites
const { mergedFavorites, isFavorite } = useKaptionFavorites();

// Resolve merged favorites to actual objects using props data
// This ensures reactivity - when favorites change, this re-computes
const resolvedFavorites = computed(() => {
  return mergedFavorites.value
    .map(fav => {
      // Map favorite type to the correct prop and type name
      const typeMap = {
        inbox: { data: props.channels, type: 'inbox' },
        channel: { data: props.channels, type: 'inbox' },
        label: { data: props.labels, type: 'label' },
        team: { data: props.teams, type: 'team' },
        folder: { data: props.folders, type: 'folder' },
        assistant: { data: props.assistants, type: 'assistant' },
      };

      const mapping = typeMap[fav.type];
      if (!mapping) return null;

      // eslint-disable-next-line eqeqeq
      const item = mapping.data.find(i => i.id == fav.id);
      if (!item) return null;

      return {
        ...item,
        type: mapping.type,
        resolved: item,
      };
    })
    .filter(Boolean);
});

// Helper to check if an item is favorited
// Uses the composable's isFavorite function for reactivity
function isItemFavorited(type, id) {
  // Map 'inbox' to 'channel' for backward compatibility with composable
  const composableType = type === 'channel' ? 'inbox' : type;
  return isFavorite(composableType, id);
}

// Handler to toggle favorite status
function handleToggleFavorite(e, type, item) {
  e.stopPropagation();
  emit('toggleFavorite', {
    type,
    item,
    isFavorited: isItemFavorited(type, item.id),
  });
}

// ============================================
// PINNABLE CATEGORIES FEATURE
// ============================================
const { uiSettings, updateUISettings } = useUISettings();

// Default pinned categories - only favorites by default
const DEFAULT_PINNED_CATEGORIES = ['favorites'];

// Computed: which categories are pinned
const pinnedCategories = computed(
  () => uiSettings.value?.topbar_pinned_categories || DEFAULT_PINNED_CATEGORIES
);

// Check if a category is pinned
function isCategoryPinned(category) {
  return pinnedCategories.value.includes(category);
}

// Toggle pin state for a category
function toggleCategoryPin(category, event) {
  event.stopPropagation(); // Don't trigger submenu navigation

  // Favorites is always pinned - cannot be unpinned
  if (category === 'favorites') return;

  const current = [...pinnedCategories.value];
  const next = current.includes(category)
    ? current.filter(c => c !== category)
    : [...current, category];

  // Ensure favorites is always included
  if (!next.includes('favorites')) {
    next.unshift('favorites');
  }

  updateUISettings({
    topbar_pinned_categories: next,
  });
}

// Track which category row is being hovered (for pin icon visibility)
const hoveredCategory = ref(null);

// ============================================
// LANGUAGE SETTINGS
// ============================================
// Get account default locale
const accountDefaultLocale = computed(
  () => currentAccount.value?.locale || 'en'
);

// Supported languages - only these 8 are shown in the topbar selector
const SUPPORTED_LANGUAGE_CODES = [
  'en',
  'es',
  'pt',
  'fr',
  'it',
  'de',
  'tr',
  'zh',
];

// Build language options from enabled languages + "Use Account Default" option
const languages = computed(() => {
  const defaultOption = {
    code: '',
    name: t('KAPTION_PLATFORM.SETTINGS.LANGUAGE_DEFAULT'),
    isDefault: true,
  };

  // Filter enabledLanguages to only include supported languages
  const langOptions = (enabledLanguages || [])
    .filter(lang => SUPPORTED_LANGUAGE_CODES.includes(lang.iso_639_1_code))
    .map(lang => ({
      code: lang.iso_639_1_code,
      name: lang.name,
      isDefault: false,
    }));

  return [defaultOption, ...langOptions];
});

// Status filters
const statusFilters = ['open', 'resolved', 'snoozed', 'all'];

// State
const filterMode = ref(props.initialFilter);
const statusFilter = ref(props.initialStatusFilter);
const panelState = ref('collapsed');
const cameFromExpanded = ref(false);
const slideDirection = ref('forward');

// Watch for external changes to the filter props and sync internal state
watch(
  () => props.initialFilter,
  newFilter => {
    if (newFilter !== filterMode.value) {
      filterMode.value = newFilter;
    }
  }
);

watch(
  () => props.initialStatusFilter,
  newStatus => {
    if (newStatus !== statusFilter.value) {
      statusFilter.value = newStatus;
    }
  }
);

// Recent items
const recentItems = ref([]);

// Initialize recent items from props
onMounted(() => {
  const items = [];
  if (props.favorites.length > 0) {
    items.push({ type: 'favorite', item: props.favorites[0] });
  }
  if (props.labels.length > 2) {
    items.push({ type: 'label', item: props.labels[2] });
  }
  if (props.folders.length > 1) {
    items.push({ type: 'folder', item: props.folders[1] });
  }
  recentItems.value = items;
});

// Internal selection state (for dropdown clicks)
const internalSelectedFavorite = ref(null);
const internalSelectedFolder = ref(null);
const internalSelectedLabel = ref(null);
const internalSelectedChannel = ref(null);
const internalSelectedTeam = ref(null);
const internalSelectedAssistant = ref(null);

// Computed: Merge internal state with props (props from route navigation take precedence)
// This ensures icons show whether selection comes from dropdown OR sidebar navigation
const activeSelectedFavorite = computed(() => internalSelectedFavorite.value);
const activeSelectedTeam = computed(
  () => props.selectedTeam || internalSelectedTeam.value
);
const activeSelectedChannel = computed(
  () => props.selectedChannel || internalSelectedChannel.value
);
const activeSelectedLabel = computed(
  () => props.selectedLabel || internalSelectedLabel.value
);
const activeSelectedFolder = computed(
  () => props.selectedFolder || internalSelectedFolder.value
);
const activeSelectedAssistant = computed(
  () => props.selectedAssistant || internalSelectedAssistant.value
);

// Computed: Check if the currently selected item (from any category) is also in favorites
// This is used to highlight the favorites icon when filtering by a favorited item
const isSelectedItemFavorited = computed(() => {
  // Check if selected team is favorited
  if (activeSelectedTeam.value) {
    if (isItemFavorited('team', activeSelectedTeam.value.id)) return true;
  }
  // Check if selected channel is favorited
  if (activeSelectedChannel.value) {
    if (isItemFavorited('inbox', activeSelectedChannel.value.id)) return true;
  }
  // Check if selected label is favorited
  if (activeSelectedLabel.value) {
    if (isItemFavorited('label', activeSelectedLabel.value.id)) return true;
  }
  // Check if selected folder is favorited
  if (activeSelectedFolder.value) {
    if (isItemFavorited('folder', activeSelectedFolder.value.id)) return true;
  }
  // Check if selected assistant is favorited
  if (activeSelectedAssistant.value) {
    if (isItemFavorited('assistant', activeSelectedAssistant.value.id))
      return true;
  }
  return false;
});

// ============================================
// FEATURE FLAGS FOR SETTINGS VISIBILITY
// ============================================
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

// Computed: show/hide settings based on feature flags
const showSendSettings = computed(() =>
  isKaptionFlagEnabled('KAPTION_SHOW_SEND_SETTINGS')
);
const messageBatchingEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_MESSAGE_BATCHING')
);

const showLiveTranslationSetting = computed(() =>
  isKaptionFlagEnabled('KAPTION_LIVE_TRANSLATION')
);

const showAiSuggestionsSetting = computed(() =>
  isKaptionFlagEnabled('KAPTION_AI_SUGGESTIONS')
);

// ============================================
// SETTINGS STATE
// ============================================
// NOTICE: KAPTION-INTEGRATION - Flat delivery settings, persisted per user.
const deliverySettingsOpen = ref(false);
const deliveryMode = computed({
  get: () =>
    resolveKaptionDeliveryMode(uiSettings.value, {
      batchingEnabled: messageBatchingEnabled.value,
    }),
  set: value =>
    updateUISettings({
      kaption_delivery_mode: value,
      kaption_undo_send_enabled: value === KAPTION_DELIVERY_MODES.UNDO,
    }),
});
const deliveryModes = computed(() =>
  messageBatchingEnabled.value
    ? [
        KAPTION_DELIVERY_MODES.IMMEDIATE,
        KAPTION_DELIVERY_MODES.UNDO,
        KAPTION_DELIVERY_MODES.PAIR,
        KAPTION_DELIVERY_MODES.TRIO,
        KAPTION_DELIVERY_MODES.BUNDLE,
      ]
    : [KAPTION_DELIVERY_MODES.IMMEDIATE, KAPTION_DELIVERY_MODES.UNDO]
);
const deliveryModeLabel = mode =>
  ({
    immediate: t('KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.immediate'),
    undo: t('KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.undo'),
    combine_2: t('KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.combine_2'),
    combine_3: t('KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.combine_3'),
    combine_5: t('KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.combine_5'),
  })[mode];
// Undo duration options (in seconds)
const UNDO_DURATION_OPTIONS = [3, 5, 10, 15];

// Undo send duration - persisted to ui_settings
const undoDuration = computed({
  get: () => uiSettings.value?.kaption_undo_send_duration ?? 15,
  set: value => updateUISettings({ kaption_undo_send_duration: value }),
});

// Language selection - persisted to ui_settings.locale
// Empty string means "use account default"
const selectedLanguage = computed({
  get: () => uiSettings.value?.locale ?? '',
  set: async value => {
    if (value === '') {
      // Clear preference to use account default
      await updateUISettings({ locale: null });
      locale.value = accountDefaultLocale.value;
    } else {
      // Validate the language code
      const valid = (enabledLanguages || []).some(
        l => l.iso_639_1_code === value
      );
      if (valid) {
        await updateUISettings({ locale: value });
        locale.value = value;
      }
    }
  },
});

// Get the effective language code for display (resolves "default" to actual language)
const effectiveLanguageCode = computed(() => {
  return selectedLanguage.value || accountDefaultLocale.value;
});

// Enter to Send - persisted to ui_settings (same backend as main settings)
const enterToSend = computed({
  get: () => uiSettings.value?.enter_to_send_enabled ?? true,
  set: value => updateUISettings({ enter_to_send_enabled: value }),
});

// Live Translation - persisted to ui_settings (master switch for the feature)
// When disabled, the feature is hidden from the action pill entirely
const liveTranslation = computed({
  get: () => uiSettings.value?.kaption_live_translation_enabled ?? true,
  set: value => {
    updateUISettings({ kaption_live_translation_enabled: value });
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.1 (master toggle)
    trackUsage(USAGE_ANALYTICS_EVENTS.LIVE_TRANSLATION_TOGGLED, {
      scope: 'master',
      enabled: Boolean(value),
    });
  },
});

// AI Suggestions - persisted to ui_settings (master switch for the feature)
// When disabled, the feature is hidden from the action pill entirely
const aiSuggestions = computed({
  get: () => uiSettings.value?.kaption_ai_suggestions_enabled ?? true,
  set: value => updateUISettings({ kaption_ai_suggestions_enabled: value }),
});

// Write Notes by Default - persisted to ui_settings
const writeNotesDefault = computed({
  get: () => uiSettings.value?.kaption_write_notes_default ?? false,
  set: value => updateUISettings({ kaption_write_notes_default: value }),
});

// Detect platform for keyboard shortcut hints
const isMac = computed(
  () =>
    typeof navigator !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform)
);

// Container ref for click outside detection
const containerRef = ref(null);

// Horizontal scroll state for category icons
const iconsScrollRef = ref(null);
const showLeftGradient = ref(false);
const showRightGradient = ref(false);

const updateIconsScrollState = () => {
  if (!iconsScrollRef.value) return;
  const { scrollLeft, scrollWidth, clientWidth } = iconsScrollRef.value;
  showLeftGradient.value = scrollLeft > 5;
  showRightGradient.value =
    scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 5;
};

const onIconsScroll = () => {
  updateIconsScrollState();
};

// Channel icon mapping (using Tailwind iconify classes)
const channelIconClass = {
  whatsapp: 'i-lucide-message-square',
  email: 'i-lucide-mail',
  telegram: 'i-lucide-send',
  instagram: 'i-lucide-at-sign',
  sms: 'i-lucide-phone',
};

function getChannelIconClass(iconType) {
  return channelIconClass[iconType] || 'i-lucide-message-square';
}

// Filter info helper - matching original design icons
function getFilterInfo(filter) {
  const info = {
    all: {
      iconClass: 'i-lucide-layers',
      label: t('KAPTION_PLATFORM.FILTERS.ALL'),
      color: 'sky',
    },
    unattended: {
      iconClass: 'i-lucide-message-circle-warning',
      label: t('KAPTION_PLATFORM.FILTERS.UNATTENDED'),
      color: 'rose',
    },
    mine: {
      iconClass: 'i-lucide-user-check',
      label: t('KAPTION_PLATFORM.FILTERS.MINE'),
      color: 'emerald',
    },
    unassigned: {
      iconClass: 'i-lucide-circle-dashed',
      label: t('KAPTION_PLATFORM.FILTERS.UNASSIGNED'),
      color: 'amber',
    },
    mentions: {
      iconClass: 'i-lucide-at-sign',
      label: t('KAPTION_PLATFORM.FILTERS.MENTIONS'),
      color: 'purple',
    },
    // NOTICE: KAPTION-INTEGRATION - Unread filter pill (no icon by design —
    // Cris asked to drop it so the pill leads with just the label + count).
    // SID: unread-filter.FILTER_PILL.2 unread-filter.TAB.4
    unread: {
      iconClass: '',
      label: t('KAPTION_PLATFORM.FILTERS.UNREAD'),
      color: 'cyan',
    },
  };
  return info[filter];
}

// Status filter info helper
function getStatusFilterInfo(status) {
  const info = {
    open: {
      iconClass: 'i-lucide-circle',
      label: t('KAPTION_PLATFORM.STATUS.OPEN'),
      color: 'blue',
    },
    resolved: {
      iconClass: 'i-lucide-check-circle',
      label: t('KAPTION_PLATFORM.STATUS.RESOLVED'),
      color: 'emerald',
    },
    snoozed: {
      iconClass: 'i-lucide-alarm-clock',
      label: t('KAPTION_PLATFORM.STATUS.SNOOZED'),
      color: 'amber',
    },
    all: {
      iconClass: 'i-lucide-layers',
      label: t('KAPTION_PLATFORM.STATUS.ALL'),
      color: 'purple',
    },
  };
  return info[status];
}

// Dynamic class helpers for filter colors
function getFilterBgClass(filter) {
  const colors = {
    all: props.isDarkMode ? 'bg-kpt-sky-soft' : 'bg-kpt-sky-light',
    unattended: props.isDarkMode ? 'bg-kpt-rose-soft' : 'bg-kpt-rose-light',
    mine: props.isDarkMode ? 'bg-kpt-emerald-soft' : 'bg-kpt-emerald-light',
    unassigned: props.isDarkMode ? 'bg-kpt-amber-soft' : 'bg-kpt-amber-light',
    mentions: props.isDarkMode ? 'bg-kpt-purple-soft' : 'bg-kpt-purple-light',
    // NOTICE: KAPTION-INTEGRATION - Unread filter pill
    unread: props.isDarkMode ? 'bg-kpt-cyan-soft' : 'bg-kpt-cyan-light',
  };
  return colors[filter];
}

function getFilterTextClass(filter) {
  const colors = {
    all: props.isDarkMode ? 'text-kpt-sky' : 'text-kpt-sky',
    unattended: props.isDarkMode ? 'text-kpt-rose' : 'text-kpt-rose',
    mine: props.isDarkMode ? 'text-kpt-emerald' : 'text-kpt-emerald',
    unassigned: props.isDarkMode ? 'text-kpt-amber' : 'text-kpt-amber',
    mentions: props.isDarkMode ? 'text-kpt-purple' : 'text-kpt-purple',
    // NOTICE: KAPTION-INTEGRATION - Unread filter pill
    unread: props.isDarkMode ? 'text-kpt-cyan' : 'text-kpt-cyan',
  };
  return colors[filter];
}

function getStatusBgClass(status) {
  if (status === 'open') return ''; // Open doesn't show
  const colors = {
    resolved: props.isDarkMode ? 'bg-kpt-emerald-soft' : 'bg-kpt-emerald-light',
    snoozed: props.isDarkMode ? 'bg-kpt-amber-soft' : 'bg-kpt-amber-light',
    all: props.isDarkMode ? 'bg-kpt-indigo-soft' : 'bg-kpt-indigo-light',
  };
  return colors[status];
}

function getStatusTextClass(status) {
  const colors = {
    open: props.isDarkMode ? 'text-kpt-sky' : 'text-kpt-sky',
    resolved: props.isDarkMode ? 'text-kpt-emerald' : 'text-kpt-emerald',
    snoozed: props.isDarkMode ? 'text-kpt-amber' : 'text-kpt-amber',
    all: props.isDarkMode ? 'text-kpt-indigo' : 'text-kpt-indigo',
  };
  return colors[status];
}

function getStatusTabBgClass(status, isActive) {
  if (!isActive) return '';
  const colors = {
    open: props.isDarkMode ? 'bg-kpt-dark-700' : 'bg-kpt-sky-light',
    resolved: props.isDarkMode ? 'bg-kpt-dark-700' : 'bg-kpt-emerald-light',
    snoozed: props.isDarkMode ? 'bg-kpt-dark-700' : 'bg-kpt-amber-light',
    all: props.isDarkMode ? 'bg-kpt-dark-700' : 'bg-kpt-indigo-light',
  };
  return colors[status];
}

// Add to recents
function addToRecents(newItem) {
  const existingIndex = recentItems.value.findIndex(
    r => r.type === newItem.type && r.item.id === newItem.item.id
  );
  if (existingIndex !== -1) {
    return;
  }
  recentItems.value = [newItem, ...recentItems.value].slice(0, 3);
}

// Click outside handler
function handleClickOutside(event) {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    panelState.value = 'collapsed';
    cameFromExpanded.value = false;
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  // Initialize horizontal scroll state for icons
  updateIconsScrollState();
  window.addEventListener('resize', updateIconsScrollState);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('resize', updateIconsScrollState);
});

// Handlers
function handleFilterClick(e) {
  e.stopPropagation();
  // Don't cycle if disabled
  if (props.disableCyclingFilter) {
    return;
  }

  // Extended filter cycle for "All Conversations" view OR when on mentions/unattended
  // Order: all → unattended → mine → unassigned → unread → mentions → all
  // NOTICE: KAPTION-INTEGRATION - Unread is part of the extended cycle.
  if (
    props.isAllConversationsView ||
    props.conversationType === 'mention' ||
    props.conversationType === 'unattended'
  ) {
    // SID: unread-filter.FILTER_PILL.1
    const extendedCycle = {
      all: 'unattended',
      unattended: 'mine',
      mine: 'unassigned',
      unassigned: 'unread',
      unread: 'mentions',
      mentions: 'all',
    };
    const nextFilter = extendedCycle[filterMode.value] || 'all';
    filterMode.value = nextFilter;

    // Navigate to special routes for mentions/unattended
    if (nextFilter === 'unattended') {
      emit('navigateToUnattended');
    } else if (nextFilter === 'mentions') {
      emit('navigateToMentions');
    } else {
      // For all/mine/unassigned/unread, navigate back to regular conversations
      emit('navigateToAllConversations', nextFilter);
    }
    return;
  }

  // Standard filter cycle for category views (team, channel, label, folder, assistant)
  // NOTICE: KAPTION-INTEGRATION - Unread is part of the basic cycle too.
  // Order: all → mine → unassigned → unread → all
  const basicCycle = {
    all: 'mine',
    mine: 'unassigned',
    unassigned: 'unread',
    unread: 'all',
  };
  const nextFilter = basicCycle[filterMode.value] || 'all';
  filterMode.value = nextFilter;
  emit('filterChange', nextFilter);
}

// Computed: filter button tooltip
const filterTooltip = computed(() => {
  if (props.disableCyclingFilter) {
    return t('KAPTION_PLATFORM.TOOLTIPS.FILTER_DISABLED');
  }
  return t('KAPTION_PLATFORM.TOOLTIPS.FILTER_CYCLE');
});

function handleStatusFilterSelect(status) {
  statusFilter.value = status;
  emit('statusFilterChange', status);
}

function handleStatusFilterClick(e) {
  e.stopPropagation();
  const statusCycle = {
    open: 'resolved',
    resolved: 'snoozed',
    snoozed: 'all',
    all: 'open',
  };
  const next = statusCycle[statusFilter.value] || 'open';
  statusFilter.value = next;
  emit('statusFilterChange', next);
}

function handleSortChange(sortValue) {
  emit('sortChange', sortValue);
}

function handleFavoriteItemClick(item) {
  addToRecents({ type: 'favorite', item });
  internalSelectedFavorite.value =
    internalSelectedFavorite.value?.id === item.id ? null : item;
  internalSelectedFolder.value = null;
  internalSelectedLabel.value = null;
  internalSelectedChannel.value = null;
  panelState.value = 'collapsed';
  emit('favoriteClick', item);
}

function handleFolderItemClick(item) {
  addToRecents({ type: 'folder', item });
  internalSelectedFolder.value =
    internalSelectedFolder.value?.id === item.id ? null : item;
  internalSelectedFavorite.value = null;
  internalSelectedLabel.value = null;
  internalSelectedChannel.value = null;
  panelState.value = 'collapsed';
  emit('folderClick', item);
}

function handleLabelItemClick(item) {
  addToRecents({ type: 'label', item });
  internalSelectedLabel.value =
    internalSelectedLabel.value?.id === item.id ? null : item;
  internalSelectedFavorite.value = null;
  internalSelectedFolder.value = null;
  internalSelectedChannel.value = null;
  panelState.value = 'collapsed';
  emit('labelClick', item);
}

function handleChannelItemClick(item) {
  addToRecents({ type: 'channel', item });
  internalSelectedChannel.value =
    internalSelectedChannel.value?.id === item.id ? null : item;
  internalSelectedFavorite.value = null;
  internalSelectedFolder.value = null;
  internalSelectedLabel.value = null;
  panelState.value = 'collapsed';
  emit('channelClick', item);
}

function handleFavoriteButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedFavorite.value) {
    internalSelectedFavorite.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'favorites';
  }
}

function handleFolderButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedFolder.value) {
    internalSelectedFolder.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'folders';
  }
}

function handleLabelButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedLabel.value) {
    internalSelectedLabel.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'labels';
  }
}

function handleChannelButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedChannel.value) {
    internalSelectedChannel.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'channels';
  }
}

function handleTeamItemClick(item) {
  addToRecents({ type: 'team', item });
  internalSelectedTeam.value =
    internalSelectedTeam.value?.id === item.id ? null : item;
  internalSelectedFavorite.value = null;
  internalSelectedFolder.value = null;
  internalSelectedLabel.value = null;
  internalSelectedChannel.value = null;
  internalSelectedAssistant.value = null;
  panelState.value = 'collapsed';
  emit('teamClick', item);
}

function handleTeamButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedTeam.value) {
    internalSelectedTeam.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'teams';
  }
}

function handleAssistantItemClick(item) {
  addToRecents({ type: 'assistant', item });
  internalSelectedAssistant.value =
    internalSelectedAssistant.value?.id === item.id ? null : item;
  internalSelectedFavorite.value = null;
  internalSelectedFolder.value = null;
  internalSelectedLabel.value = null;
  internalSelectedChannel.value = null;
  internalSelectedTeam.value = null;
  panelState.value = 'collapsed';
  emit('assistantClick', item);
}

function handleAssistantButtonClick(e) {
  e.stopPropagation();
  if (internalSelectedAssistant.value) {
    internalSelectedAssistant.value = null;
  } else {
    cameFromExpanded.value = false;
    panelState.value = 'assistants';
  }
}

function handleDropdownClick(e) {
  e.stopPropagation();
  panelState.value =
    panelState.value === 'collapsed' ? 'expanded' : 'collapsed';
}

function navigateTo(panel, fromExpanded = false) {
  slideDirection.value = 'forward';
  cameFromExpanded.value = fromExpanded;
  panelState.value = panel;
}

function navigateBack(panel) {
  slideDirection.value = 'backward';
  panelState.value = panel;
}

function selectLanguage(code) {
  selectedLanguage.value = code;
  navigateBack('settings');
}

// Computed for current language (finds the matching language object)
const currentLanguage = computed(() =>
  languages.value.find(l => l.code === selectedLanguage.value)
);

// Computed transition name based on slide direction
const transitionName = computed(() =>
  slideDirection.value === 'forward' ? 'slide-left' : 'slide-right'
);

// Inactive button classes - using kpt- colors for Chatwoot compatibility
const inactiveButtonClass = computed(() =>
  props.isDarkMode
    ? 'text-kpt-text-muted-dark hover:text-kpt-text-secondary-dark hover:bg-kpt-dark-700'
    : 'text-kpt-text-muted-light hover:text-kpt-text-secondary-light hover:bg-kpt-light-200'
);

// Text color classes - using kpt- colors
const primaryTextClass = computed(() =>
  props.isDarkMode
    ? 'text-kpt-text-primary-dark'
    : 'text-kpt-text-primary-light'
);

const secondaryTextClass = computed(() =>
  props.isDarkMode
    ? 'text-kpt-text-secondary-dark'
    : 'text-kpt-text-secondary-light'
);

// Hover background classes - using kpt- colors
const hoverBgClass = computed(() =>
  props.isDarkMode ? 'hover:bg-kpt-dark-700' : 'hover:bg-kpt-light-200'
);

const subtleHoverBgClass = computed(() =>
  props.isDarkMode ? 'hover:bg-kpt-dark-600' : 'hover:bg-kpt-light-100'
);

// Divider class - using kpt- colors
const dividerClass = computed(() =>
  props.isDarkMode ? 'bg-kpt-border-dark' : 'bg-kpt-border-light'
);

// Icon color classes - using kpt- colors for Chatwoot compatibility
const starIconClass = computed(() => 'text-kpt-amber');

const folderIconClass = computed(() => 'text-kpt-indigo');

const labelIconClass = computed(() => 'text-kpt-rose');

const channelIconColorClass = computed(() => 'text-kpt-cyan');

const teamIconClass = computed(() => 'text-kpt-purple');

const assistantIconClass = computed(() => 'text-kpt-emerald');

const mutedIconClass = computed(() =>
  props.isDarkMode
    ? 'text-kpt-text-secondary-dark'
    : 'text-kpt-text-secondary-light'
);

// Inactive icon color - using kpt- colors
const inactiveIconClass = computed(() =>
  props.isDarkMode ? 'text-kpt-text-muted-dark' : 'text-kpt-text-muted-light'
);
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <!-- Top Bar - Always Visible -->
    <!-- Use transparent background to inherit from parent (ChatList uses bg-n-solid-1) -->
    <div class="flex items-center gap-1 px-3 py-2">
      <!-- Person Filter Toggle with cycling hint -->
      <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for pill-shaped appearance -->
      <button
        v-tooltip.bottom="filterTooltip"
        class="group flex items-center gap-1.5 px-3 h-8 rounded-full transition-all duration-200"
        :class="[
          getFilterBgClass(filterMode),
          disableCyclingFilter
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:scale-105 active:scale-95',
        ]"
        @click="handleFilterClick"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - Hide the icon slot entirely when a
             filter opts out (iconClass === ''), so the pill collapses to just
             the label and avoids an empty 16×16 gap. -->
        <span
          v-if="getFilterInfo(filterMode).iconClass"
          class="h-4 w-4"
          :class="[
            getFilterInfo(filterMode).iconClass,
            getFilterTextClass(filterMode),
          ]"
        />
        <span
          class="text-xs font-medium"
          :class="getFilterTextClass(filterMode)"
        >
          {{ getFilterInfo(filterMode).label }}
        </span>
        <!-- Cycling hint icon on hover - animates width (hidden when disabled) -->
        <span
          v-if="!disableCyclingFilter"
          class="i-lucide-repeat h-3.5 overflow-hidden transition-all duration-200 ease-out group-hover:w-3.5 group-hover:opacity-70 w-0 opacity-0"
          :class="getFilterTextClass(filterMode)"
        />
      </button>

      <!-- Status Filter Icon - only if not 'open' -->
      <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
      <button
        v-if="statusFilter !== 'open'"
        v-tooltip.bottom="getStatusFilterInfo(statusFilter).label"
        class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
        :class="getStatusBgClass(statusFilter)"
        @click="handleStatusFilterClick"
      >
        <span
          class="h-4 w-4"
          :class="[
            getStatusFilterInfo(statusFilter).iconClass,
            getStatusTextClass(statusFilter),
          ]"
        />
      </button>

      <!-- Sort Menu -->
      <KaptionSortMenu
        :model-value="currentSort"
        :is-dark-mode="isDarkMode"
        @update:model-value="handleSortChange"
      />

      <!-- Spacer -->
      <div class="flex-1 min-w-0" />

      <!-- Category Icons - Scrollable container -->
      <div class="relative flex items-center min-w-0 max-w-[200px]">
        <!-- Left gradient indicator -->
        <div
          class="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r z-10 pointer-events-none transition-opacity duration-200"
          :class="[
            showLeftGradient ? 'opacity-100' : 'opacity-0',
            props.isDarkMode
              ? 'from-kpt-dark-900 to-transparent'
              : 'from-kpt-light-50 to-transparent',
          ]"
        />
        <!-- Scrollable icons container -->
        <div
          ref="iconsScrollRef"
          class="flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth"
          @scroll="onIconsScroll"
        >
          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-tooltip.bottom="
              activeSelectedFavorite
                ? activeSelectedFavorite.name
                : t('KAPTION_PLATFORM.CATEGORIES.FAVORITES')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedFavorite || isSelectedItemFavorited
                ? props.isDarkMode
                  ? 'bg-kpt-amber-soft'
                  : 'bg-kpt-amber-soft'
                : inactiveButtonClass
            "
            @click="handleFavoriteButtonClick"
          >
            <svg
              class="h-4 w-4 transition-transform duration-200"
              :class="
                activeSelectedFavorite || isSelectedItemFavorited
                  ? starIconClass
                  : inactiveIconClass
              "
              :fill="
                activeSelectedFavorite || isSelectedItemFavorited
                  ? 'currentColor'
                  : 'none'
              "
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <polygon
                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
              />
            </svg>
          </button>

          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-if="isCategoryPinned('folders') || activeSelectedFolder"
            v-tooltip.bottom="
              activeSelectedFolder
                ? activeSelectedFolder.name
                : t('KAPTION_PLATFORM.CATEGORIES.FOLDERS')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedFolder
                ? props.isDarkMode
                  ? 'bg-kpt-indigo-soft'
                  : 'bg-kpt-indigo-soft'
                : inactiveButtonClass
            "
            @click="handleFolderButtonClick"
          >
            <span
              class="i-lucide-folder h-4 w-4 transition-transform duration-200"
              :class="
                activeSelectedFolder ? folderIconClass : inactiveIconClass
              "
            />
          </button>

          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-if="isCategoryPinned('labels') || activeSelectedLabel"
            v-tooltip.bottom="
              activeSelectedLabel
                ? activeSelectedLabel.name
                : t('KAPTION_PLATFORM.CATEGORIES.LABELS')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedLabel
                ? props.isDarkMode
                  ? 'bg-kpt-rose-soft'
                  : 'bg-kpt-rose-soft'
                : inactiveButtonClass
            "
            @click="handleLabelButtonClick"
          >
            <span
              class="i-lucide-tag h-4 w-4 transition-transform duration-200"
              :class="activeSelectedLabel ? labelIconClass : inactiveIconClass"
            />
          </button>

          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-if="isCategoryPinned('channels') || activeSelectedChannel"
            v-tooltip.bottom="
              activeSelectedChannel
                ? activeSelectedChannel.name
                : t('KAPTION_PLATFORM.CATEGORIES.CHANNELS')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedChannel
                ? props.isDarkMode
                  ? 'bg-kpt-cyan-soft'
                  : 'bg-kpt-cyan-soft'
                : inactiveButtonClass
            "
            @click="handleChannelButtonClick"
          >
            <span
              class="h-4 w-4 transition-transform duration-200"
              :class="[
                activeSelectedChannel
                  ? getChannelIconClass(activeSelectedChannel.iconType)
                  : 'i-lucide-message-square',
                activeSelectedChannel
                  ? channelIconColorClass
                  : inactiveIconClass,
              ]"
            />
          </button>

          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-if="
              (isCategoryPinned('teams') && teams.length > 0) ||
              activeSelectedTeam
            "
            v-tooltip.bottom="
              activeSelectedTeam
                ? activeSelectedTeam.name
                : t('KAPTION_PLATFORM.CATEGORIES.TEAMS')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedTeam
                ? props.isDarkMode
                  ? 'bg-kpt-purple-soft'
                  : 'bg-kpt-purple-soft'
                : inactiveButtonClass
            "
            @click="handleTeamButtonClick"
          >
            <span
              class="i-lucide-users h-4 w-4 transition-transform duration-200"
              :class="activeSelectedTeam ? teamIconClass : inactiveIconClass"
            />
          </button>

          <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
          <button
            v-if="
              (isCategoryPinned('assistants') && assistants.length > 0) ||
              activeSelectedAssistant
            "
            v-tooltip.bottom="
              activeSelectedAssistant
                ? activeSelectedAssistant.name
                : t('KAPTION_PLATFORM.CATEGORIES.ASSISTANTS')
            "
            class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
            :class="
              activeSelectedAssistant
                ? props.isDarkMode
                  ? 'bg-kpt-emerald-soft'
                  : 'bg-kpt-emerald-soft'
                : inactiveButtonClass
            "
            @click="handleAssistantButtonClick"
          >
            <span
              class="i-lucide-bot h-4 w-4 transition-transform duration-200"
              :class="
                activeSelectedAssistant ? assistantIconClass : inactiveIconClass
              "
            />
          </button>
        </div>
        <!-- Right gradient indicator -->
        <div
          class="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l z-10 pointer-events-none transition-opacity duration-200"
          :class="[
            showRightGradient ? 'opacity-100' : 'opacity-0',
            props.isDarkMode
              ? 'from-kpt-dark-900 to-transparent'
              : 'from-kpt-light-50 to-transparent',
          ]"
        />
      </div>

      <!-- Dropdown Button - NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
      <button
        class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
        :class="
          panelState === 'expanded'
            ? props.isDarkMode
              ? 'bg-kpt-dark-700'
              : 'bg-kpt-light-200'
            : inactiveButtonClass
        "
        @click="handleDropdownClick"
      >
        <span
          class="i-lucide-chevron-down h-4 w-4 transition-transform duration-200"
          :class="[
            panelState === 'expanded' ? 'rotate-180' : '',
            panelState === 'expanded'
              ? props.isDarkMode
                ? 'text-white'
                : 'text-gray-700'
              : inactiveIconClass,
          ]"
        />
      </button>
    </div>

    <!-- Floating Dropdown Panel - NOTICE: KAPTION-INTEGRATION - rounded-3xl for rounder appearance -->
    <div
      v-if="panelState !== 'collapsed'"
      class="absolute top-full left-0 right-0 z-50 mt-1 mx-2 rounded-3xl overflow-hidden shadow-lg dropdown-panel"
      :class="[
        props.isDarkMode
          ? 'bg-kpt-dark-800/80 shadow-black/20 border border-kpt-border-dark backdrop-blur-xl'
          : 'bg-kpt-light-50/90 shadow-gray-200/40 border border-kpt-border-light backdrop-blur-xl',
      ]"
      @click.stop
    >
      <Transition :name="transitionName" mode="out-in">
        <!-- Main Expanded Panel -->
        <div
          v-if="panelState === 'expanded'"
          key="expanded"
          class="flex flex-col gap-1 p-2 pb-3"
        >
          <!-- Status Filter Tabs -->
          <div class="flex items-center gap-1 p-1 rounded-2xl mb-2">
            <button
              v-for="status in statusFilters"
              :key="status"
              class="flex items-center justify-center gap-1.5 rounded-full text-xs font-medium transition-all duration-200"
              :class="[
                statusFilter === status ? 'flex-1 px-2 py-1.5' : 'w-8 h-8',
                statusFilter === status
                  ? getStatusTabBgClass(status, true)
                  : props.isDarkMode
                    ? 'hover:bg-white/5'
                    : 'hover:bg-gray-50',
              ]"
              @click="handleStatusFilterSelect(status)"
            >
              <span
                class="h-3.5 w-3.5"
                :class="[
                  getStatusFilterInfo(status).iconClass,
                  statusFilter === status
                    ? getStatusTextClass(status)
                    : props.isDarkMode
                      ? 'text-white/40'
                      : 'text-gray-400',
                ]"
              />
              <span
                v-if="statusFilter === status"
                :class="getStatusTextClass(status)"
              >
                {{ getStatusFilterInfo(status).label }}
              </span>
            </button>
          </div>

          <!-- Recent Items Section -->
          <div>
            <p
              class="text-[10px] uppercase tracking-wider px-3 py-1"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.RECENT') }}
            </p>
            <template
              v-for="(recent, index) in recentItems"
              :key="`recent-${index}-${recent.item.id}`"
            >
              <button
                v-if="recent.type === 'favorite'"
                class="flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
                :class="hoverBgClass"
                @click="handleFavoriteItemClick(recent.item)"
              >
                <svg
                  class="h-4 w-4 text-kpt-amber"
                  fill="currentColor"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
                <span class="text-sm" :class="primaryTextClass">
                  {{ recent.item.name }}
                </span>
              </button>
              <button
                v-else-if="recent.type === 'folder'"
                class="flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
                :class="hoverBgClass"
                @click="handleFolderItemClick(recent.item)"
              >
                <span
                  class="i-lucide-folder h-4 w-4"
                  :class="folderIconClass"
                />
                <span class="text-sm" :class="primaryTextClass">
                  {{ recent.item.name }}
                </span>
              </button>
              <button
                v-else-if="recent.type === 'label'"
                class="flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
                :class="hoverBgClass"
                @click="handleLabelItemClick(recent.item)"
              >
                <div
                  class="w-4 h-4 rounded-full"
                  :style="{ backgroundColor: recent.item.color }"
                />
                <span class="text-sm" :class="primaryTextClass">
                  {{ recent.item.name }}
                </span>
              </button>
              <button
                v-else
                class="flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
                :class="hoverBgClass"
                @click="handleChannelItemClick(recent.item)"
              >
                <span
                  class="h-4 w-4"
                  :class="[
                    getChannelIconClass(recent.item.iconType),
                    mutedIconClass,
                  ]"
                />
                <span class="text-sm" :class="primaryTextClass">
                  {{ recent.item.name }}
                </span>
              </button>
            </template>
          </div>

          <div class="h-px mx-2 mb-1" :class="dividerClass" />

          <!-- Navigation -->
          <button
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @click="navigateTo('favorites', true)"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-star h-4 w-4" :class="starIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.FAVORITES')
              }}</span>
            </div>
            <span
              class="i-lucide-chevron-right h-4 w-4"
              :class="secondaryTextClass"
            />
          </button>

          <button
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @mouseenter="hoveredCategory = 'folders'"
            @mouseleave="hoveredCategory = null"
            @click="navigateTo('folders', true)"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-folder h-4 w-4" :class="folderIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.FOLDERS')
              }}</span>
            </div>
            <div class="flex items-center gap-1">
              <!-- Pin icon - only visible on hover -->
              <button
                v-if="hoveredCategory === 'folders'"
                v-tooltip.top="
                  isCategoryPinned('folders')
                    ? t('KAPTION_PLATFORM.TOOLTIPS.UNPIN')
                    : t('KAPTION_PLATFORM.TOOLTIPS.PIN')
                "
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity"
                :class="subtleHoverBgClass"
                @click.stop="toggleCategoryPin('folders', $event)"
              >
                <span
                  class="size-4"
                  :class="[
                    isCategoryPinned('folders')
                      ? 'i-lucide-pin text-kpt-amber'
                      : 'i-lucide-pin-off ' + secondaryTextClass,
                  ]"
                />
              </button>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </div>
          </button>

          <button
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @mouseenter="hoveredCategory = 'labels'"
            @mouseleave="hoveredCategory = null"
            @click="navigateTo('labels', true)"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-tag h-4 w-4" :class="mutedIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.LABELS')
              }}</span>
            </div>
            <div class="flex items-center gap-1">
              <!-- Pin icon - only visible on hover -->
              <button
                v-if="hoveredCategory === 'labels'"
                v-tooltip.top="
                  isCategoryPinned('labels')
                    ? t('KAPTION_PLATFORM.TOOLTIPS.UNPIN')
                    : t('KAPTION_PLATFORM.TOOLTIPS.PIN')
                "
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity"
                :class="subtleHoverBgClass"
                @click.stop="toggleCategoryPin('labels', $event)"
              >
                <span
                  class="size-4"
                  :class="[
                    isCategoryPinned('labels')
                      ? 'i-lucide-pin text-kpt-amber'
                      : 'i-lucide-pin-off ' + secondaryTextClass,
                  ]"
                />
              </button>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </div>
          </button>

          <button
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @mouseenter="hoveredCategory = 'channels'"
            @mouseleave="hoveredCategory = null"
            @click="navigateTo('channels', true)"
          >
            <div class="flex items-center gap-3">
              <span
                class="i-lucide-message-square h-4 w-4"
                :class="mutedIconClass"
              />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.CHANNELS')
              }}</span>
            </div>
            <div class="flex items-center gap-1">
              <!-- Pin icon - only visible on hover -->
              <button
                v-if="hoveredCategory === 'channels'"
                v-tooltip.top="
                  isCategoryPinned('channels')
                    ? t('KAPTION_PLATFORM.TOOLTIPS.UNPIN')
                    : t('KAPTION_PLATFORM.TOOLTIPS.PIN')
                "
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity"
                :class="subtleHoverBgClass"
                @click.stop="toggleCategoryPin('channels', $event)"
              >
                <span
                  class="size-4"
                  :class="[
                    isCategoryPinned('channels')
                      ? 'i-lucide-pin text-kpt-amber'
                      : 'i-lucide-pin-off ' + secondaryTextClass,
                  ]"
                />
              </button>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </div>
          </button>

          <button
            v-if="teams.length > 0"
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @mouseenter="hoveredCategory = 'teams'"
            @mouseleave="hoveredCategory = null"
            @click="navigateTo('teams', true)"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-users h-4 w-4" :class="mutedIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.TEAMS')
              }}</span>
            </div>
            <div class="flex items-center gap-1">
              <!-- Pin icon - only visible on hover -->
              <button
                v-if="hoveredCategory === 'teams'"
                v-tooltip.top="
                  isCategoryPinned('teams')
                    ? t('KAPTION_PLATFORM.TOOLTIPS.UNPIN')
                    : t('KAPTION_PLATFORM.TOOLTIPS.PIN')
                "
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity"
                :class="subtleHoverBgClass"
                @click.stop="toggleCategoryPin('teams', $event)"
              >
                <span
                  class="size-4"
                  :class="[
                    isCategoryPinned('teams')
                      ? 'i-lucide-pin text-kpt-amber'
                      : 'i-lucide-pin-off ' + secondaryTextClass,
                  ]"
                />
              </button>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </div>
          </button>

          <button
            v-if="assistants.length > 0"
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @mouseenter="hoveredCategory = 'assistants'"
            @mouseleave="hoveredCategory = null"
            @click="navigateTo('assistants', true)"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-bot h-4 w-4" :class="mutedIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.CATEGORIES.ASSISTANTS')
              }}</span>
            </div>
            <div class="flex items-center gap-1">
              <!-- Pin icon - only visible on hover -->
              <button
                v-if="hoveredCategory === 'assistants'"
                v-tooltip.top="
                  isCategoryPinned('assistants')
                    ? t('KAPTION_PLATFORM.TOOLTIPS.UNPIN')
                    : t('KAPTION_PLATFORM.TOOLTIPS.PIN')
                "
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity"
                :class="subtleHoverBgClass"
                @click.stop="toggleCategoryPin('assistants', $event)"
              >
                <span
                  class="size-4"
                  :class="[
                    isCategoryPinned('assistants')
                      ? 'i-lucide-pin text-kpt-amber'
                      : 'i-lucide-pin-off ' + secondaryTextClass,
                  ]"
                />
              </button>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </div>
          </button>

          <div
            v-if="showSettingsPanel"
            class="h-px mx-2 my-1"
            :class="dividerClass"
          />

          <button
            v-if="showSettingsPanel"
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200"
            :class="hoverBgClass"
            @click="
              deliverySettingsOpen = showSendSettings;
              navigateTo('settings', true);
            "
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-settings h-4 w-4" :class="mutedIconClass" />
              <span class="text-sm" :class="primaryTextClass">{{
                t('KAPTION_PLATFORM.SETTINGS.TITLE')
              }}</span>
            </div>
            <span
              class="i-lucide-chevron-right h-4 w-4"
              :class="secondaryTextClass"
            />
          </button>
        </div>

        <!-- Favorites Sub-Panel -->
        <div
          v-else-if="panelState === 'favorites'"
          key="favorites"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.FAVORITES') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.FAVORITES') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.FILTER_DROPDOWN.4 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in resolvedFavorites"
              :key="item.id"
              class="flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleFavoriteItemClick(item)"
            >
              <svg
                class="h-5 w-5 text-kpt-amber flex-shrink-0"
                fill="currentColor"
                stroke="currentColor"
                stroke-width="1.5"
                viewBox="0 0 24 24"
              >
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                />
              </svg>
              <span class="text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
            </button>
          </div>
        </div>

        <!-- Folders Sub-Panel -->
        <div
          v-else-if="panelState === 'folders'"
          key="folders"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.FOLDERS') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.FOLDERS') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.FILTER_DROPDOWN.4 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in folders"
              :key="item.id"
              class="group flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleFolderItemClick(item)"
            >
              <span class="i-lucide-folder h-4 w-4" :class="folderIconClass" />
              <span class="flex-1 text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
              <!-- Star icon for favoriting -->
              <span
                v-tooltip.top="
                  isItemFavorited('folder', item.id)
                    ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                    : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
                "
                role="button"
                tabindex="0"
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity cursor-pointer"
                :class="[
                  isItemFavorited('folder', item.id)
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                ]"
                @click="handleToggleFavorite($event, 'folder', item)"
                @keydown.enter="handleToggleFavorite($event, 'folder', item)"
                @keydown.space.prevent="
                  handleToggleFavorite($event, 'folder', item)
                "
              >
                <svg
                  class="size-4 transition-all duration-200"
                  :class="
                    isItemFavorited('folder', item.id)
                      ? 'text-kpt-amber'
                      : secondaryTextClass
                  "
                  :fill="
                    isItemFavorited('folder', item.id) ? 'currentColor' : 'none'
                  "
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Labels Sub-Panel -->
        <div
          v-else-if="panelState === 'labels'"
          key="labels"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.LABELS') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.LABELS') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - With many labels (e.g. Amar) this
               list grew past the viewport while the panel root is
               overflow-hidden, so the tail was clipped and unreachable. The
               list now scrolls INSIDE the dropdown with the header pinned;
               same wrapper applied to every collection sub-panel.
               SID: chat-ui-fixes.FILTER_DROPDOWN.1 chat-ui-fixes.FILTER_DROPDOWN.2 chat-ui-fixes.FILTER_DROPDOWN.3 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in labels"
              :key="item.id"
              class="group flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleLabelItemClick(item)"
            >
              <div
                class="w-4 h-4 rounded-full"
                :style="{ backgroundColor: item.color }"
              />
              <span class="flex-1 text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
              <!-- Star icon for favoriting -->
              <span
                v-tooltip.top="
                  isItemFavorited('label', item.id)
                    ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                    : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
                "
                role="button"
                tabindex="0"
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity cursor-pointer"
                :class="[
                  isItemFavorited('label', item.id)
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                ]"
                @click="handleToggleFavorite($event, 'label', item)"
                @keydown.enter="handleToggleFavorite($event, 'label', item)"
                @keydown.space.prevent="
                  handleToggleFavorite($event, 'label', item)
                "
              >
                <svg
                  class="size-4 transition-all duration-200"
                  :class="
                    isItemFavorited('label', item.id)
                      ? 'text-kpt-amber'
                      : secondaryTextClass
                  "
                  :fill="
                    isItemFavorited('label', item.id) ? 'currentColor' : 'none'
                  "
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Channels Sub-Panel -->
        <div
          v-else-if="panelState === 'channels'"
          key="channels"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.CHANNELS') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.CHANNELS') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.FILTER_DROPDOWN.4 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in channels"
              :key="item.id"
              class="group flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleChannelItemClick(item)"
            >
              <span
                class="h-4 w-4"
                :class="[getChannelIconClass(item.iconType), mutedIconClass]"
              />
              <span class="flex-1 text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
              <!-- Star icon for favoriting -->
              <span
                v-tooltip.top="
                  isItemFavorited('inbox', item.id)
                    ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                    : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
                "
                role="button"
                tabindex="0"
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity cursor-pointer"
                :class="[
                  isItemFavorited('inbox', item.id)
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                ]"
                @click="handleToggleFavorite($event, 'inbox', item)"
                @keydown.enter="handleToggleFavorite($event, 'inbox', item)"
                @keydown.space.prevent="
                  handleToggleFavorite($event, 'inbox', item)
                "
              >
                <svg
                  class="size-4 transition-all duration-200"
                  :class="
                    isItemFavorited('inbox', item.id)
                      ? 'text-kpt-amber'
                      : secondaryTextClass
                  "
                  :fill="
                    isItemFavorited('inbox', item.id) ? 'currentColor' : 'none'
                  "
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Teams Sub-Panel -->
        <div
          v-else-if="panelState === 'teams'"
          key="teams"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.TEAMS') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.TEAMS') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.FILTER_DROPDOWN.4 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in teams"
              :key="item.id"
              class="group flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleTeamItemClick(item)"
            >
              <span class="i-lucide-users h-4 w-4" :class="mutedIconClass" />
              <span class="flex-1 text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
              <!-- Star icon for favoriting -->
              <span
                v-tooltip.top="
                  isItemFavorited('team', item.id)
                    ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                    : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
                "
                role="button"
                tabindex="0"
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity cursor-pointer"
                :class="[
                  isItemFavorited('team', item.id)
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                ]"
                @click="handleToggleFavorite($event, 'team', item)"
                @keydown.enter="handleToggleFavorite($event, 'team', item)"
                @keydown.space.prevent="
                  handleToggleFavorite($event, 'team', item)
                "
              >
                <svg
                  class="size-4 transition-all duration-200"
                  :class="
                    isItemFavorited('team', item.id)
                      ? 'text-kpt-amber'
                      : secondaryTextClass
                  "
                  :fill="
                    isItemFavorited('team', item.id) ? 'currentColor' : 'none'
                  "
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Assistants Sub-Panel -->
        <div
          v-else-if="panelState === 'assistants'"
          key="assistants"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.CATEGORIES.ASSISTANTS') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.CATEGORIES.ASSISTANTS') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.FILTER_DROPDOWN.4 -->
          <div class="overflow-y-auto max-h-[min(60vh,26rem)]">
            <button
              v-for="item in assistants"
              :key="item.id"
              class="group flex items-center gap-3 px-3 py-2 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
              :class="hoverBgClass"
              @click="handleAssistantItemClick(item)"
            >
              <span class="i-lucide-bot h-4 w-4" :class="mutedIconClass" />
              <span class="flex-1 text-sm" :class="primaryTextClass">
                {{ item.name }}
              </span>
              <!-- Star icon for favoriting -->
              <span
                v-tooltip.top="
                  isItemFavorited('assistant', item.id)
                    ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                    : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
                "
                role="button"
                tabindex="0"
                class="flex-shrink-0 size-5 p-0 flex items-center justify-center rounded transition-opacity cursor-pointer"
                :class="[
                  isItemFavorited('assistant', item.id)
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                ]"
                @click="handleToggleFavorite($event, 'assistant', item)"
                @keydown.enter="handleToggleFavorite($event, 'assistant', item)"
                @keydown.space.prevent="
                  handleToggleFavorite($event, 'assistant', item)
                "
              >
                <svg
                  class="size-4 transition-all duration-200"
                  :class="
                    isItemFavorited('assistant', item.id)
                      ? 'text-kpt-amber'
                      : secondaryTextClass
                  "
                  :fill="
                    isItemFavorited('assistant', item.id)
                      ? 'currentColor'
                      : 'none'
                  "
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Settings Sub-Panel -->
        <div
          v-else-if="panelState === 'settings'"
          key="settings"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              v-if="cameFromExpanded"
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('expanded')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.SETTINGS.TITLE') }}
              </span>
            </button>
            <span
              v-else
              class="text-[10px] uppercase tracking-wider"
              :class="secondaryTextClass"
            >
              {{ t('KAPTION_PLATFORM.SETTINGS.TITLE') }}
            </span>
            <button
              v-if="!cameFromExpanded"
              class="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors duration-200"
              :class="hoverBgClass"
              @click="panelState = 'collapsed'"
            >
              <span class="i-lucide-x h-3 w-3" :class="secondaryTextClass" />
            </button>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Flat, focused delivery configuration. -->
          <div v-if="showSendSettings">
            <button
              v-if="!deliverySettingsOpen"
              data-testid="setting-delivery-mode"
              class="flex items-start justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
              :class="hoverBgClass"
              @click="deliverySettingsOpen = true"
            >
              <span class="flex items-start gap-3">
                <span
                  class="i-lucide-send h-4 w-4 flex-shrink-0 mt-0.5"
                  :class="mutedIconClass"
                />
                <span>
                  <span class="text-sm" :class="primaryTextClass">
                    {{ t('KAPTION_PLATFORM.SETTINGS.DELIVERY.TITLE') }}
                  </span>
                  <span class="block text-[10px]" :class="secondaryTextClass">
                    {{ deliveryModeLabel(deliveryMode) }}
                  </span>
                </span>
              </span>
              <span
                class="i-lucide-chevron-right h-4 w-4"
                :class="secondaryTextClass"
              />
            </button>
            <section
              v-else
              class="px-3 py-2"
              :aria-label="t('KAPTION_PLATFORM.SETTINGS.DELIVERY.SETTINGS')"
            >
              <button
                class="mb-2 flex items-center gap-1 text-xs"
                :class="secondaryTextClass"
                @click="deliverySettingsOpen = false"
              >
                <span class="i-lucide-chevron-left h-3.5 w-3.5" />
                {{ t('KAPTION_PLATFORM.SETTINGS.DELIVERY.SETTINGS') }}
              </button>
              <div
                role="radiogroup"
                :aria-label="t('KAPTION_PLATFORM.SETTINGS.DELIVERY.TITLE')"
                class="space-y-1"
              >
                <button
                  v-for="mode in deliveryModes"
                  :key="mode"
                  :data-testid="`delivery-mode-${mode}`"
                  role="radio"
                  :aria-checked="deliveryMode === mode"
                  class="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left"
                  :class="
                    deliveryMode === mode ? 'bg-kpt-emerald-soft' : hoverBgClass
                  "
                  @click="deliveryMode = mode"
                >
                  <span class="text-xs font-medium" :class="primaryTextClass">{{
                    deliveryModeLabel(mode)
                  }}</span>
                  <span
                    v-if="deliveryMode === mode"
                    class="i-lucide-check h-4 w-4 text-kpt-emerald"
                  />
                </button>
              </div>
              <div
                v-if="deliveryMode === KAPTION_DELIVERY_MODES.UNDO"
                class="mt-3 border-t pt-3"
                :class="
                  props.isDarkMode
                    ? 'border-kpt-border-dark'
                    : 'border-kpt-border-light'
                "
              >
                <p class="mb-2 text-[10px]" :class="secondaryTextClass">
                  {{ t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS') }}
                </p>
                <div
                  class="flex gap-1"
                  role="group"
                  :aria-label="
                    t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS')
                  "
                >
                  <button
                    v-for="duration in UNDO_DURATION_OPTIONS"
                    :key="duration"
                    :aria-pressed="undoDuration === duration"
                    class="rounded-md px-2 py-1 text-xs"
                    :class="
                      undoDuration === duration
                        ? 'bg-kpt-emerald text-white'
                        : hoverBgClass
                    "
                    @click="undoDuration = duration"
                  >
                    {{
                      $t(
                        'KAPTION_PLATFORM.SETTINGS.UNDO_MODE.DURATION_OPTION',
                        { seconds: duration }
                      )
                    }}
                  </button>
                </div>
              </div>
            </section>
          </div>

          <!-- Enter to Send - Persisted to ui_settings -->
          <button
            data-testid="setting-enter-to-send"
            class="flex items-start justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
            :class="hoverBgClass"
            @click="enterToSend = !enterToSend"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-corner-down-left h-4 w-4 flex-shrink-0 mt-0.5"
                :class="mutedIconClass"
              />
              <div>
                <span class="text-sm" :class="primaryTextClass">{{
                  t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.TITLE')
                }}</span>
                <p class="text-[10px]" :class="secondaryTextClass">
                  {{
                    enterToSend
                      ? t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.NEW_LINE', {
                          key: isMac ? '\u2318+Enter' : 'Ctrl+Enter',
                        })
                      : t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.TO_SEND', {
                          key: isMac ? '\u2318+Enter' : 'Ctrl+Enter',
                        })
                  }}
                </p>
              </div>
            </div>
            <div
              class="relative h-5 w-9 rounded-full transition-colors duration-200 flex-shrink-0 mt-0.5"
              :class="
                enterToSend
                  ? 'bg-kpt-emerald'
                  : props.isDarkMode
                    ? 'bg-kpt-dark-600'
                    : 'bg-kpt-light-300'
              "
            >
              <span
                class="absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow-sm transition-all duration-200"
                :class="[
                  enterToSend ? 'translate-x-4' : 'translate-x-0',
                  props.isDarkMode ? 'bg-white' : 'bg-white',
                ]"
              />
            </div>
          </button>

          <!-- Live Translation - Hidden when KAPTION_LIVE_TRANSLATION flag is disabled -->
          <button
            v-if="showLiveTranslationSetting"
            data-testid="setting-live-translation"
            class="flex items-start justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
            :class="hoverBgClass"
            @click="liveTranslation = !liveTranslation"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-languages h-4 w-4 flex-shrink-0 mt-0.5"
                :class="mutedIconClass"
              />
              <div>
                <span class="text-sm" :class="primaryTextClass">{{
                  t('KAPTION_PLATFORM.SETTINGS.LIVE_TRANSLATION.TITLE')
                }}</span>
                <p class="text-[10px]" :class="secondaryTextClass">
                  {{
                    t('KAPTION_PLATFORM.SETTINGS.LIVE_TRANSLATION.DESCRIPTION')
                  }}
                </p>
              </div>
            </div>
            <div
              class="relative h-5 w-9 rounded-full transition-colors duration-200 flex-shrink-0 mt-0.5"
              :class="
                liveTranslation
                  ? 'bg-kpt-emerald'
                  : props.isDarkMode
                    ? 'bg-kpt-dark-600'
                    : 'bg-kpt-light-300'
              "
            >
              <span
                class="absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow-sm transition-all duration-200"
                :class="[
                  liveTranslation ? 'translate-x-4' : 'translate-x-0',
                  props.isDarkMode ? 'bg-white' : 'bg-white',
                ]"
              />
            </div>
          </button>

          <!-- AI Suggestions - Hidden when KAPTION_AI_SUGGESTIONS flag is disabled -->
          <button
            v-if="showAiSuggestionsSetting"
            data-testid="setting-ai-suggestions"
            class="flex items-start justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
            :class="hoverBgClass"
            @click="aiSuggestions = !aiSuggestions"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-wand-2 h-4 w-4 flex-shrink-0 mt-0.5"
                :class="mutedIconClass"
              />
              <div>
                <span class="text-sm" :class="primaryTextClass">{{
                  t('KAPTION_PLATFORM.SETTINGS.AI_SUGGESTIONS.TITLE')
                }}</span>
                <p class="text-[10px]" :class="secondaryTextClass">
                  {{
                    t('KAPTION_PLATFORM.SETTINGS.AI_SUGGESTIONS.DESCRIPTION')
                  }}
                </p>
              </div>
            </div>
            <div
              class="relative h-5 w-9 rounded-full transition-colors duration-200 flex-shrink-0 mt-0.5"
              :class="
                aiSuggestions
                  ? 'bg-kpt-emerald'
                  : props.isDarkMode
                    ? 'bg-kpt-dark-600'
                    : 'bg-kpt-light-300'
              "
            >
              <span
                class="absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow-sm transition-all duration-200"
                :class="[
                  aiSuggestions ? 'translate-x-4' : 'translate-x-0',
                  props.isDarkMode ? 'bg-white' : 'bg-white',
                ]"
              />
            </div>
          </button>

          <!-- Write Notes by Default - Persisted to ui_settings -->
          <button
            data-testid="setting-write-notes-default"
            class="flex items-start justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
            :class="hoverBgClass"
            @click="writeNotesDefault = !writeNotesDefault"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-lock h-4 w-4 flex-shrink-0 mt-0.5"
                :class="mutedIconClass"
              />
              <div>
                <span class="text-sm" :class="primaryTextClass">{{
                  t('KAPTION_PLATFORM.SETTINGS.WRITE_NOTES.TITLE')
                }}</span>
                <p class="text-[10px]" :class="secondaryTextClass">
                  {{ t('KAPTION_PLATFORM.SETTINGS.WRITE_NOTES.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div
              class="relative h-5 w-9 rounded-full transition-colors duration-200 flex-shrink-0 mt-0.5"
              :class="
                writeNotesDefault
                  ? 'bg-kpt-emerald'
                  : props.isDarkMode
                    ? 'bg-kpt-dark-600'
                    : 'bg-kpt-light-300'
              "
            >
              <span
                class="absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow-sm transition-all duration-200"
                :class="[
                  writeNotesDefault ? 'translate-x-4' : 'translate-x-0',
                  props.isDarkMode ? 'bg-white' : 'bg-white',
                ]"
              />
            </div>
          </button>

          <div class="h-px mx-2 my-1" :class="dividerClass" />

          <!-- Language Selection -->
          <button
            class="flex items-center justify-between px-3 py-2 rounded-2xl cursor-pointer transition-colors duration-200 w-full text-left"
            :class="hoverBgClass"
            @click="panelState = 'language'"
          >
            <div class="flex items-center gap-3">
              <KaptionCircleFlag
                :language-code="effectiveLanguageCode"
                size="size-4"
              />
              <span class="text-sm" :class="primaryTextClass">
                {{
                  currentLanguage?.name ||
                  t('KAPTION_PLATFORM.SETTINGS.LANGUAGE_DEFAULT')
                }}
              </span>
            </div>
            <span
              class="i-lucide-chevron-right h-4 w-4"
              :class="secondaryTextClass"
            />
          </button>
        </div>

        <!-- Language Sub-Panel -->
        <div
          v-else-if="panelState === 'language'"
          key="language"
          class="flex flex-col p-2 pb-3"
        >
          <div class="flex items-center justify-between px-3 py-1 mb-1">
            <button
              class="flex items-center gap-1 cursor-pointer transition-colors duration-200 rounded-full px-2 py-0.5"
              :class="hoverBgClass"
              @click="navigateBack('settings')"
            >
              <span
                class="i-lucide-chevron-left h-3 w-3"
                :class="secondaryTextClass"
              />
              <span
                class="text-[10px] uppercase tracking-wider"
                :class="secondaryTextClass"
              >
                {{ t('KAPTION_PLATFORM.SETTINGS.LANGUAGE') }}
              </span>
            </button>
          </div>
          <button
            v-for="lang in languages"
            :key="lang.code || 'default'"
            class="flex items-center gap-3 px-3 py-1.5 rounded-2xl cursor-pointer w-full text-left transition-colors duration-200"
            :class="[
              selectedLanguage === lang.code
                ? props.isDarkMode
                  ? 'bg-white/10'
                  : 'bg-gray-100'
                : hoverBgClass,
            ]"
            @click="selectLanguage(lang.code)"
          >
            <!-- Globe icon for "Use Account Default", flag for specific languages -->
            <span
              v-if="lang.isDefault"
              class="i-lucide-globe h-4 w-4"
              :class="mutedIconClass"
            />
            <KaptionCircleFlag
              v-else
              :language-code="lang.code"
              size="size-4"
            />
            <span class="text-sm" :class="primaryTextClass">
              {{ lang.name }}
            </span>
            <!-- Show account language indicator for default option -->
            <span
              v-if="lang.isDefault"
              class="text-xs ml-auto"
              :class="secondaryTextClass"
            >
              ({{ accountDefaultLocale }})
            </span>
          </button>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
/* Dropdown panel animation */
.dropdown-panel {
  animation: dropdownEnter 0.1s ease-out;
}

@keyframes dropdownEnter {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Slide Left (forward navigation) */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.05s ease-out;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(8px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}

/* Slide Right (backward navigation) */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.05s ease-out;
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-8px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(8px);
}
</style>
